import React, { ReactNode, useEffect } from 'react';
import { Box, Stack, Typography, SxProps } from '@mui/material';
import { usePageIntro } from './PageIntroContext';

/**
 * Standard "visually hidden" rule — element stays in the accessibility tree
 * and document outline but takes no visible space. Used to keep the H1 in
 * the DOM even when the page intro is collapsed.
 */
const SR_ONLY_SX: SxProps = {
  border: 0,
  clip: 'rect(0, 0, 0, 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px',
};

interface IPageIntroProps {
  title: string;
  description?: ReactNode;
  /** Right-side controls (Import / Export / Create…). Stay visible when collapsed. */
  actions?: ReactNode;
  /** Optional eyebrow above H1 (category badge, CEFR pill, status chip…).
   *  Hidden together with the rest of the intro when collapsed. */
  eyebrow?: ReactNode;
  /** Tall right-aligned hero widget — stat trio, progress card, resume card.
   *  Sits next to the title-block on desktop and disappears when collapsed. */
  aside?: ReactNode;
  /** Inline content rendered next to the description (StatPill-sized widgets).
   *  Hidden together with the intro when collapsed. */
  children?: ReactNode;
  /** Wide content rendered below the description-row, full container width
   *  (e.g. multi-card stats grid). Hidden together with the intro. */
  footer?: ReactNode;
  /** Wrap the whole hero (title + description + aside + footer) in a
   *  white card with a soft border. Use on pages where the hero should
   *  read as a self-contained panel above the body. */
  surface?: boolean;
  /** Override styling on the h1 itself. Merged on top of the base
   *  typography so callers can tweak color/weight without losing the
   *  default size + responsive scaling. */
  titleSx?: SxProps;
}

const PageIntro: React.FC<IPageIntroProps> = ({
  title,
  description,
  actions,
  eyebrow,
  aside,
  children,
  footer,
  surface,
  titleSx,
}) => {
  const { collapsed, registerIntro } = usePageIntro();

  // Register on mount → makes the breadcrumb toggle appear. The registration
  // is a stable callback, so this effect runs once per mount/unmount.
  useEffect(() => registerIntro(), [registerIntro]);

  if (collapsed) {
    return (
      <>
        {/* H1 stays for screen readers / SEO even when visually hidden. */}
        <Typography component={'h1'} sx={SR_ONLY_SX}>
          {title}
        </Typography>
        {actions && (
          <Stack
            direction={'row'}
            alignItems={'center'}
            justifyContent={'flex-end'}
            flexWrap={'wrap'}
            spacing={1.25}
            useFlexGap
            sx={{ mb: 1.5, mt: 1.5 }}
          >
            {actions}
          </Stack>
        )}
      </>
    );
  }

  const titleRow = (
    <Stack
      direction={'row'}
      alignItems={'center'}
      justifyContent={'space-between'}
      flexWrap={'wrap'}
      sx={{
        gap: { xs: 2, md: 4 },
        mb: surface ? (footer ? 1.75 : 0) : footer ? 1.75 : 2.25,
        mt: surface ? 0 : 1.5,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {eyebrow && <Box sx={{ mb: 1.5 }}>{eyebrow}</Box>}
        <Typography
          component={'h1'}
          sx={{
            fontSize: { xs: 24, md: 30, lg: 34 },
            fontWeight: 800,
            lineHeight: 1.15,
            m: 0,
            ...titleSx,
          }}
        >
          {title}
        </Typography>
        {(description || children) && (
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2.5,
              justifyContent: 'space-between',
              mt: 1.25,
            }}
          >
            {description && (
              <Typography
                sx={{
                  color: 'text.secondary',
                  fontSize: { xs: 16, md: 18 },
                  flex: '1 1 320px',
                  m: 0,
                  maxWidth: 560,
                }}
              >
                {description}
              </Typography>
            )}
            {children && <Box sx={{ flex: 'none' }}>{children}</Box>}
          </Box>
        )}
      </Box>
      {aside && <Box sx={{ flex: 'none' }}>{aside}</Box>}
      {actions && (
        <Stack
          direction={'row'}
          alignItems={'center'}
          flexWrap={'wrap'}
          spacing={1.25}
          useFlexGap
        >
          {actions}
        </Stack>
      )}
    </Stack>
  );

  if (surface) {
    return (
      <Box
        sx={{
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          mb: 2.25,
          mt: 1.5,
          p: { xs: 2, md: '20px 24px' },
        }}
      >
        {titleRow}
        {footer && <Box>{footer}</Box>}
      </Box>
    );
  }

  return (
    <>
      {titleRow}
      {footer && <Box sx={{ mb: 2.25 }}>{footer}</Box>}
    </>
  );
};

export default PageIntro;
